import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";

const CAPABILITIES = [
  { label: "Positioning", items: [
    { to: "/services/brand-strategy", l: "Brand Strategy" },
    { to: "/services/digital-audit", l: "Digital Audit" },
    { to: "/services/remote-cmo", l: "Remote CMO" }
  ]},
  { label: "Storytelling", items: [
    { to: "/services/brand-storytelling", l: "Brand Films & Storytelling" },
    { to: "/ad-studio", l: "Ad Studio" },
    { to: "/services/content-marketing", l: "Content Marketing" }
  ]},
  { label: "Authority Building", items: [
    { to: "/services/founder-branding", l: "Founder Branding" },
    { to: "/services/personal-branding", l: "Personal Branding" },
    { to: "/services/linkedin-growth", l: "LinkedIn Growth" },
    { to: "/services/ai-search-optimization", l: "AI Visibility" },
    { to: "/services/geo", l: "Generative Engine Optimization" }
  ]},
  { label: "Growth Systems", items: [
    { to: "/services/seo", l: "SEO" },
    { to: "/services/google-ads", l: "Google Ads" },
    { to: "/services/meta-ads", l: "Meta Ads" },
    { to: "/services/social-media-marketing", l: "Social Media" }
  ]}
];

const INDUSTRIES = [
  { to: "/industry/food-hospitality", l: "Hospitality" },
  { to: "/industry/real-estate", l: "Real Estate" },
  { to: "/fashion", l: "Fashion & Lifestyle" },
  { to: "/industry/healthcare-wellness", l: "Healthcare" },
  { to: "/industry/saas-technology", l: "Technology" },
  { to: "/industry/ecommerce-retail", l: "E-Commerce" },
  { to: "/industry/finance-banking", l: "Finance" },
  { to: "/industry/startups-smes", l: "Startups" },
  { to: "/industry/education-edtech", l: "Education" }
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDrop, setOpenDrop] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setMenuOpen(false); setOpenDrop(null); }, [location]);

  const navTo = (to) => {
    setOpenDrop(null);
    setMenuOpen(false);
    navigate(to);
  };

  return (
    <nav data-testid="navbar" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "navbar-scrolled backdrop-blur-xl border-b border-white/10" : "bg-transparent"}`}>
      <div className="px-6 lg:px-16 xl:px-28 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="navbar-logo">
          <img src="https://static.wixstatic.com/media/e528db_fa64245c9e6542638a0120e98d60aedf~mv2.jpg" alt="Prospera.buzz" className="h-7 w-7 object-cover" />
          <span className="text-xs tracking-[0.1em]">Prospera.buzz</span>
        </Link>

        {/* Desktop */}
        <div className="hidden lg:flex items-center gap-6 xl:gap-8">
          <button onClick={() => navTo("/")} className={`text-[10px] tracking-[0.15em] transition-colors whitespace-nowrap ${location.pathname === "/" ? "text-white" : "text-zinc-500 hover:text-white"}`}>Home</button>

          <button onClick={() => setOpenDrop(openDrop === "cap" ? null : "cap")}
            className={`text-[10px] tracking-[0.15em] transition-colors whitespace-nowrap inline-flex items-center gap-1 ${openDrop === "cap" ? "text-white" : "text-zinc-500 hover:text-white"}`}>
            Capabilities <ChevronDown className={`w-3 h-3 transition-transform ${openDrop === "cap" ? "rotate-180" : ""}`} />
          </button>

          <button onClick={() => setOpenDrop(openDrop === "ind" ? null : "ind")}
            className={`text-[10px] tracking-[0.15em] transition-colors whitespace-nowrap inline-flex items-center gap-1 ${openDrop === "ind" ? "text-white" : "text-zinc-500 hover:text-white"}`}>
            Industries <ChevronDown className={`w-3 h-3 transition-transform ${openDrop === "ind" ? "rotate-180" : ""}`} />
          </button>

          <button onClick={() => navTo("/academy")} className={`text-[10px] tracking-[0.15em] transition-colors whitespace-nowrap ${location.pathname === "/academy" ? "text-white" : "text-zinc-500 hover:text-white"}`}>Thinking</button>
          <button onClick={() => navTo("/careers")} className={`text-[10px] tracking-[0.15em] transition-colors whitespace-nowrap ${location.pathname === "/careers" ? "text-white" : "text-zinc-500 hover:text-white"}`}>Careers</button>
          <button onClick={() => navTo("/booking")} className={`text-[10px] tracking-[0.15em] transition-colors whitespace-nowrap ${location.pathname === "/booking" ? "text-white" : "text-zinc-500 hover:text-white"}`}>Contact</button>
        </div>

        <a href="https://wa.me/919041344420?text=Hi%20Prospera%2C%20I'd%20like%20to%20discuss%20a%20project." target="_blank" rel="noopener noreferrer" data-testid="navbar-whatsapp"
          className="hidden lg:inline-flex items-center gap-2 bg-white text-black px-5 py-2 text-[10px] tracking-[0.15em] hover:bg-zinc-200 transition-colors flex-shrink-0 whitespace-nowrap">
          <Phone className="w-3 h-3" /> Talk to us
        </a>

        <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden p-1" data-testid="mobile-menu-toggle">
          {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* CAPABILITIES MEGA DROPDOWN */}
      {openDrop === "cap" && (
        <div className="hidden lg:block absolute top-16 left-0 right-0 nav-dropdown-panel backdrop-blur-xl border-b border-white/10">
          <div className="px-6 lg:px-16 xl:px-28 py-10 grid grid-cols-4 gap-12 max-w-6xl">
            {CAPABILITIES.map(col => (
              <div key={col.label}>
                <p className="text-[9px] tracking-[0.3em] text-zinc-600 mb-5">{col.label}</p>
                <div className="space-y-3">
                  {col.items.map(link => (
                    <button key={link.to} onClick={() => navTo(link.to)}
                      className="block text-xs text-zinc-400 hover:text-white transition-colors tracking-wider text-left w-full">{link.l}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* INDUSTRIES DROPDOWN */}
      {openDrop === "ind" && (
        <div className="hidden lg:block absolute top-16 left-0 right-0 nav-dropdown-panel backdrop-blur-xl border-b border-white/10">
          <div className="px-6 lg:px-16 xl:px-28 py-8">
            <div className="flex flex-wrap gap-x-10 gap-y-3 max-w-4xl">
              {INDUSTRIES.map(link => (
                <button key={link.to} onClick={() => navTo(link.to)}
                  className="text-xs text-zinc-400 hover:text-white transition-colors tracking-wider">{link.l}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* MOBILE MENU */}
      {menuOpen && (
        <div className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 px-6 py-8 max-h-[80vh] overflow-y-auto mobile-menu-bg">
          <button onClick={() => navTo("/")} className="block text-xs tracking-[0.15em] text-zinc-400 hover:text-white transition-colors py-3 w-full text-left">Home</button>

          <div className="py-3">
            <p className="text-xs tracking-[0.15em] text-zinc-300 mb-3">Capabilities</p>
            <div className="pl-4 space-y-2 border-l border-white/10">
              {CAPABILITIES.map(col => (
                <div key={col.label} className="mb-4">
                  <p className="text-[9px] tracking-[0.3em] text-zinc-600 mb-2">{col.label}</p>
                  {col.items.map(link => (
                    <button key={link.to} onClick={() => navTo(link.to)}
                      className="block text-[11px] text-zinc-500 hover:text-white transition-colors tracking-wider py-1 w-full text-left">{link.l}</button>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div className="py-3">
            <p className="text-xs tracking-[0.15em] text-zinc-300 mb-3">Industries</p>
            <div className="pl-4 space-y-1 border-l border-white/10">
              {INDUSTRIES.map(link => (
                <button key={link.to} onClick={() => navTo(link.to)}
                  className="block text-[11px] text-zinc-500 hover:text-white transition-colors tracking-wider py-1 w-full text-left">{link.l}</button>
              ))}
            </div>
          </div>

          <button onClick={() => navTo("/academy")} className="block text-xs tracking-[0.15em] text-zinc-400 hover:text-white transition-colors py-3 w-full text-left">Thinking</button>
          <button onClick={() => navTo("/careers")} className="block text-xs tracking-[0.15em] text-zinc-400 hover:text-white transition-colors py-3 w-full text-left">Careers</button>
          <button onClick={() => navTo("/booking")} className="block text-xs tracking-[0.15em] text-zinc-400 hover:text-white transition-colors py-3 w-full text-left">Contact</button>

          <a href="https://wa.me/919041344420?text=Hi%20Prospera%2C%20I'd%20like%20to%20discuss%20a%20project." target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-black px-5 py-3 text-[10px] tracking-[0.15em] mt-6">
            <Phone className="w-3 h-3" /> Talk to us
          </a>
        </div>
      )}
    </nav>
  );
}
